import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

let idCounter = 0;

const TOAST_DURATION = 4000;
const UNDO_TOAST_DURATION = 10000;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const showToast = useCallback((message, type = 'success', options = {}) => {
    const { action, duration = TOAST_DURATION } = options;
    const id = ++idCounter;
    setToasts(prev => [...prev, { id, message, type, action }]);
    timersRef.current[id] = setTimeout(() => removeToast(id), duration);
    return id;
  }, [removeToast]);

  const showToastWithAction = useCallback((message, type, actionLabel, onAction, duration = UNDO_TOAST_DURATION) => {
    return showToast(message, type, {
      action: {
        label: actionLabel,
        handler: (toastId, removeFn) => {
          onAction();
          removeFn(toastId);
        },
      },
      duration,
    });
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, showToastWithAction }}>
      {children}
      <ToastList toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Graceful fallback if used outside provider
    return { showToast: () => {}, showToastWithAction: () => {} };
  }
  return ctx;
};

// ─── Toast List UI ────────────────────────────────────────────────────────────

const ICON = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

const COLOR = {
  success: {
    bg: 'var(--color-success-bg)',
    border: 'var(--color-success-border)',
    text: 'var(--color-success-text)',
    icon: 'var(--color-success-dark)',
  },
  error: {
    bg: 'var(--color-danger-bg)',
    border: 'var(--color-danger-border)',
    text: 'var(--color-danger-text)',
    icon: 'var(--color-danger)',
  },
  warning: {
    bg: 'var(--color-warning-bg)',
    border: 'var(--color-warning-border)',
    text: 'var(--color-warning-text)',
    icon: 'var(--color-warning-dark)',
  },
  info: {
    bg: 'var(--color-info-bg)',
    border: 'var(--color-info-border)',
    text: 'var(--color-info-text)',
    icon: 'var(--color-info)',
  },
};

const ToastItem = ({ toast, onRemove }) => {
  const colors = COLOR[toast.type] || COLOR.info;

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-sm)',
        padding: 'var(--space-sm) var(--space-md)',
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        maxWidth: '360px',
        pointerEvents: 'all',
        animation: 'toast-in 0.2s ease',
      }}
    >
      <span style={{
        fontWeight: 700,
        fontSize: '1rem',
        color: colors.icon,
        flexShrink: 0,
        lineHeight: 1.4,
      }}>
        {ICON[toast.type] || ICON.info}
      </span>
      <span style={{
        flex: 1,
        fontSize: 'var(--text-sm)',
        color: colors.text,
        lineHeight: 1.4,
      }}>
        {toast.message}
      </span>
      {toast.action && (
        <button
          onClick={() => toast.action.handler(toast.id, onRemove)}
          style={{
            background: 'none',
            border: `1px solid ${colors.border}`,
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            color: colors.icon,
            fontWeight: 600,
            fontSize: 'var(--text-xs)',
            padding: '2px 8px',
            lineHeight: 1.4,
            flexShrink: 0,
            alignSelf: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={() => onRemove(toast.id)}
        aria-label="Close"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: colors.text,
          opacity: 0.6,
          fontSize: '0.875rem',
          padding: 0,
          lineHeight: 1,
          flexShrink: 0,
          alignSelf: 'center',
        }}
      >
        ✕
      </button>
    </div>
  );
};

const ToastList = ({ toasts, onRemove }) => {
  if (!toasts.length) return null;

  return (
    <>
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div
        style={{
          position: 'fixed',
          bottom: 'var(--space-lg)',
          right: 'var(--space-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-sm)',
          zIndex: 9999,
          pointerEvents: 'none',
        }}
      >
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
        ))}
      </div>
    </>
  );
};

export default ToastContext;
